import { Fragment } from "react";
import { X } from "lucide-react";
import type { FormDef, FormExtra, FormField, FormOption, Spec, SpecPick } from "../../api/workorders";

// 規格勾選區(規格 §3.2)。照後端 `GET /api/work-orders/forms` 給的定義畫,新增一種工單這裡不必改。
// 單選 = radio(另有「清除」鈕,草稿可以先不選);複選 = checkbox;勾了才出現 +P / 頁數 / +文字。
// 手機可用:每個選項是一顆可換行的標籤,附帶欄位緊跟在它後面。

export interface SpecFormProps {
  form: FormDef;
  value: Spec;
  onChange: (spec: Spec) => void;
  disabled?: boolean;
}

function parseExtra(extra: FormExtra, raw: string): number | string | null {
  if (extra.kind === "text") return raw;
  if (raw.trim() === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function SpecForm({ form, value, onChange, disabled = false }: SpecFormProps) {
  const picksOf = (field: FormField): SpecPick[] => value[field.key] ?? [];

  const setPicks = (field: FormField, picks: SpecPick[]) => {
    const next: Spec = { ...value };
    if (picks.length > 0) next[field.key] = picks;
    else delete next[field.key];
    onChange(next);
  };

  const toggle = (field: FormField, option: FormOption, checked: boolean) => {
    if (!field.multi) {
      setPicks(field, checked ? [{ option: option.value }] : []);
      return;
    }
    const current = picksOf(field);
    // 照定義的順序排,不是照點的順序 —— PDF 與 Word 也是照這個順序印。
    const picks = field.options
      .filter((o) => (o.value === option.value ? checked : current.some((p) => p.option === o.value)))
      .map((o) => current.find((p) => p.option === o.value) ?? { option: o.value });
    setPicks(field, picks);
  };

  const setExtra = (field: FormField, option: FormOption, extra: FormExtra, raw: string) => {
    setPicks(field, picksOf(field).map((p) => (p.option === option.value ? ({ ...p, [extra.key]: parseExtra(extra, raw) } as SpecPick) : p)));
  };

  return (
    <div className="flex flex-col gap-3">
      {form.fields.map((field, index) => {
        const picks = picksOf(field);
        const newGroup = field.group !== "" && (index === 0 || form.fields[index - 1].group !== field.group);
        return (
          <Fragment key={field.key}>
            {newGroup ? <h4 className="m-0 mt-1 text-sm font-semibold text-[var(--text)]">{field.group}</h4> : null}
            <fieldset disabled={disabled} className="m-0 flex min-w-0 flex-wrap items-center gap-x-4 gap-y-2 rounded-[var(--radius)] border border-[var(--border)] px-3 py-2">
              <legend className="px-1 text-sm font-medium text-[var(--text-2)]">{field.label}</legend>
              {field.options.map((option) => {
                const pick = picks.find((p) => p.option === option.value);
                return (
                  <span key={option.value} className="flex flex-wrap items-center gap-1.5">
                    <label className="flex items-center gap-1.5 text-sm text-[var(--text)]">
                      <input
                        type={field.multi ? "checkbox" : "radio"}
                        name={`${form.code}-${field.key}`}
                        checked={pick !== undefined}
                        onChange={(event) => toggle(field, option, event.target.checked)}
                      />
                      {option.value}
                    </label>
                    {pick
                      ? option.extras.map((extra) => (
                          <span key={extra.key} className="flex items-center gap-1 text-sm text-[var(--text-2)]">
                            {extra.prefix.trim() ? <span>{extra.prefix.trim()}</span> : null}
                            <input
                              aria-label={`${field.label} ${option.value} ${extra.name}`}
                              type={extra.kind === "text" ? "text" : "number"}
                              inputMode={extra.kind === "integer" ? "numeric" : extra.kind === "number" ? "decimal" : undefined}
                              min={extra.kind === "text" ? undefined : 0}
                              value={pick[extra.key] ?? ""}
                              onChange={(event) => setExtra(field, option, extra, event.target.value)}
                              className={"h-8 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-2 text-sm " + (extra.kind === "text" ? "w-40" : "w-20 text-right")}
                            />
                            {extra.suffix.trim() ? <span>{extra.suffix.trim()}</span> : null}
                          </span>
                        ))
                      : null}
                  </span>
                );
              })}
              {!field.multi && picks.length > 0 ? (
                <button type="button" aria-label={`清除 ${field.label}`} onClick={() => setPicks(field, [])}
                  className="ml-auto flex min-h-8 min-w-8 items-center justify-center rounded-[var(--radius)] p-2 text-[var(--text-3)] hover:bg-[var(--surface-2)]">
                  <X aria-hidden="true" size={14} />
                </button>
              ) : null}
            </fieldset>
          </Fragment>
        );
      })}
    </div>
  );
}
